import React, { useEffect, useState, useRef } from 'react';
import { Typography } from 'interfaces/typography';
import { SessionDataResult } from 'interfaces/sessions';
import { useSelector } from 'react-redux';
import { CallDifficultyMode, CALL_DIFFICULTY_TO_HUMAN_READABLE, CallLanguage } from 'interfaces/db';
import { Play, X, Phone, PhoneOff, Mic, Settings } from 'lucide-react';
import { RootState } from '../../../store';
import { PaymentSubscriptionOption, PaymentSubscriptionOptionToPaymentLink } from "interfaces/subscriptions";
import { SubscriptionType } from "interfaces/users";
import { VAPIClient, constructAsssistantOption } from '../../../components/SynthProspect/api/vapi';
import { getServicesManager } from '../../../services';
import { VAPI_CLIENT_KEY, VAPI_LIST_KEY } from '../../../cfg/vapi';
import { usePracticeBot } from 'lib/redux/bots/session-bot-hook';

// Add a mapping for human-readable language names for the practice call modal
const LANGUAGE_TO_HUMAN_READABLE: Record<CallLanguage, string> = {
  [CallLanguage.ENGLISH]: 'English',
  [CallLanguage.SPANISH]: 'Spanish',
  [CallLanguage.FRENCH]: 'French',
  [CallLanguage.DUTCH]: 'Dutch',
  [CallLanguage.GERMAN]: 'German'
};

interface PracticeBotTestProps {
  sessionData: SessionDataResult;
}

export const PracticeBotTest: React.FC<PracticeBotTestProps> = ({ sessionData }) => {
  const sessionId = sessionData.session.session_id;
  const { 
    bot, 
    isLoaded,
    isConstructing,
    createPracticeBot 
  } = usePracticeBot(sessionId);

  // Get user and team info from Redux for access checking
  const user = useSelector((state: RootState) => state.user.value);
  const team = useSelector((state: RootState) => state.team.value);

  // VAPI client for voice calls
  const vapiClientRef = useRef<VAPIClient | null>(null);
  const lastCallStartTimeRef = useRef<{time: number} | null>(null);

  // Practice call state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [isInitializingCall, setIsInitializingCall] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState<CallDifficultyMode>(
    localStorage.getItem('lastCallDifficulty') as CallDifficultyMode || CallDifficultyMode.EASY
  );
  const [selectedLanguage, setSelectedLanguage] = useState<CallLanguage>(
    localStorage.getItem('lastCallLanguage') as CallLanguage || CallLanguage.ENGLISH
  );
  const [transcripts, setTranscripts] = useState<{role: string, transcript: string}[]>([]);
  const [activeBotCallId, setActiveBotCallId] = useState<string | null>(null);
  const [selectedInputDevice, setSelectedInputDevice] = useState<string>('');
  const [selectedOutputDevice, setSelectedOutputDevice] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcripts]);

  // Persist difficulty and language selections
  useEffect(() => {
    if (selectedDifficulty) {
      localStorage.setItem('lastCallDifficulty', selectedDifficulty);
    }
  }, [selectedDifficulty]);

  useEffect(() => {
    if (selectedLanguage) {
      localStorage.setItem('lastCallLanguage', selectedLanguage);
    }
  }, [selectedLanguage]);

  // Initialize VAPI client
  useEffect(() => {
    const initVapiClient = async () => {
      vapiClientRef.current = new VAPIClient(VAPI_CLIENT_KEY);
      vapiClientRef.current.setEventHandlers({
        onMessage: (response) => {
          if (response.transcript && response.role && response.transcriptType === 'final') {
            setTranscripts(prev => {
              // If we have previous transcripts and the last one has the same role
              if (prev.length > 0 && prev[prev.length - 1].role === response.role) {
                // Create new array with all but last entry
                const newTranscripts = prev.slice(0, -1);
                // Add combined entry
                return [...newTranscripts, {
                  role: response.role as string,
                  transcript: prev[prev.length - 1].transcript + ' ' + response.transcript
                }];
              }
              // Otherwise add as new entry
              return [...prev, {
                role: response.role as string,
                transcript: response.transcript as string
              }];
            });
          }
        },
        onError: (error) => {
          console.error('VAPI error:', error);
          handleEndCall();
        }
      });
    };

    initVapiClient();
    return () => {
      if (vapiClientRef.current) {
        vapiClientRef.current.endCall().catch(console.error);
      }
    };
  }, []);

  // Check if user has access to practice calls
  const checkPracticeAccess = () => {
    // In a real implementation, this would check with a backend service
    // For now, we'll simulate this by checking subscription status
    const hasAccess = user?.autodialer_paid || team?.subscription_type === SubscriptionType.TEAM;
    return hasAccess;
  };

  // Handle selecting audio devices
  const handleSelectAudioInput = async (deviceId: string) => {
    if (!vapiClientRef.current) return;
    try {
      await vapiClientRef.current.setInputDevicesAsync({ audioDeviceId: deviceId });
      setSelectedInputDevice(deviceId);
    } catch (error) {
      console.error('Error setting input device:', error);
      alert('Failed to set audio input device');
    }
  };

  const handleSelectAudioOutput = async (deviceId: string) => {
    if (!vapiClientRef.current) return;
    try {
      await vapiClientRef.current.setOutputDeviceAsync(deviceId);
      setSelectedOutputDevice(deviceId);
    } catch (error) {
      console.error('Error setting output device:', error);
      alert('Failed to set audio output device');
    }
  };

  // Handle starting a practice call with VAPI
  const handleStartPractice = async () => {
    if (!vapiClientRef.current || !bot) return;

    // Prevent double-clicks
    const now = Date.now();
    if (lastCallStartTimeRef.current && now - lastCallStartTimeRef.current.time < 2000) {
      return;
    }
    lastCallStartTimeRef.current = { time: now };

    setIsInitializingCall(true);
    try {
      const hasAccess = checkPracticeAccess();
      if (!hasAccess) {
        setShowUpgradeModal(true);
        setIsInitializingCall(false);
        return;
      }
      
      // Initiate a synthetic session
      const call = await getServicesManager().initiateSynthSession(bot.bot_id, selectedDifficulty);
      if (typeof call === 'string') {
        setShowUpgradeModal(true);
        setIsInitializingCall(false);
        return;
      }
      
      if (!call) {
        alert('Failed to start call. Please try again.');
        setIsInitializingCall(false);
        return;
      }

      // Start the call with VAPI using the bot's configuration
      await vapiClientRef.current.startCall(constructAsssistantOption(
        bot,
        [], // No emotions for practice bot
        call.bot_call_id,
        selectedDifficulty,
        selectedLanguage,
        true, // Has practice access
        undefined // No skill
      ));

      // Set active call data
      setActiveBotCallId(call.bot_call_id);
      setTranscripts([]); // Clear any previous transcripts
      
      // Show practice modal and set call as active
      setShowPracticeModal(true);
      setIsCallActive(true);
      
    } catch (error) {
      console.error('Error starting practice:', error);
      alert('Failed to start practice call. Please try again.');
    } finally {
      setIsInitializingCall(false);
    }
  };

  const handleEndCall = async () => {
    if (!vapiClientRef.current) return;
    try {
      // End the VAPI call
      await vapiClientRef.current.endCall();
      setIsCallActive(false);
      setTranscripts([]);
      setActiveBotCallId(null);
      setShowPracticeModal(false);
    } catch (error) {
      console.error('Error ending call:', error);
    }
  };

  // Replace with proper component
  const AudioDeviceSelector: React.FC<{
    selectedInputDevice: string;
    selectedOutputDevice: string;
    isCallActive: boolean;
    onSelectInput: (deviceId: string) => void;
    onSelectOutput: (deviceId: string) => void;
  }> = ({ 
    selectedInputDevice, 
    selectedOutputDevice, 
    isCallActive,
    onSelectInput,
    onSelectOutput
  }) => {
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
    const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    
    useEffect(() => {
      const getDevices = async () => {
        setIsLoading(true);
        try {
          // Request permission for audio devices
          await navigator.mediaDevices.getUserMedia({ audio: true });
          
          // Get all devices
          const devices = await navigator.mediaDevices.enumerateDevices();
          
          // Filter for audio input devices
          const audioInputs = devices.filter(device => device.kind === 'audioinput');
          setAudioDevices(audioInputs);
          
          // Filter for audio output devices
          const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
          setOutputDevices(audioOutputs);
          
          // Set default devices if not already set
          if (!selectedInputDevice && audioInputs.length > 0) {
            onSelectInput(audioInputs[0].deviceId);
          }
          
          if (!selectedOutputDevice && audioOutputs.length > 0) {
            onSelectOutput(audioOutputs[0].deviceId);
          }
          
        } catch (error) {
          console.error('Error getting media devices:', error);
        }
        setIsLoading(false);
      };
      
      getDevices();
    }, []);
    
    return (
      <div className="grid gap-3 p-3 bg-gray-800 rounded-lg mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-medium">Audio Settings</h3>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
        
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Microphone</label>
            <select
              value={selectedInputDevice}
              onChange={(e) => onSelectInput(e.target.value)}
              disabled={isLoading || isCallActive}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-1.5 text-sm"
            >
              {audioDevices.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                </option>
              ))}
              {audioDevices.length === 0 && (
                <option disabled>No microphones found</option>
              )}
            </select>
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1">Speaker</label>
            <select
              value={selectedOutputDevice}
              onChange={(e) => onSelectOutput(e.target.value)}
              disabled={isLoading || isCallActive}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-1.5 text-sm"
            >
              {outputDevices.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Speaker ${outputDevices.indexOf(device) + 1}`}
                </option>
              ))}
              {outputDevices.length === 0 && (
                <option disabled>No speakers found</option>
              )}
            </select>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (!isLoaded) {
    return (
      <div className="px-3 py-2">
        <div className="flex items-center justify-center py-3">
          <div className="flex items-center gap-2">
            <div className="relative w-5 h-5">
              <div className="absolute top-0 left-0 w-full h-full border-2 border-gray-200 rounded-full"></div>
              <div className="absolute top-0 left-0 w-full h-full border-2 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
            </div>
            <Typography variant="mediumParagraph" color="gray">
              Searching for existing bots...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  // Bot construction in progress
  if (isConstructing) {
    return (
      <div className="px-3 py-2">
        <div className="flex items-center justify-center py-3">
          <div className="flex items-center gap-2">
            <div className="relative w-5 h-5">
              <div className="absolute top-0 left-0 w-full h-full border-2 border-gray-200 rounded-full"></div>
              <div className="absolute top-0 left-0 w-full h-full border-2 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
            </div>
            <Typography variant="mediumParagraph" color="gray">
              Creating practice bot...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  // No bot exists yet
  if (!bot) {
    return (
      <div className="px-3 py-2">
        <div className="flex items-center justify-center py-3">
          <div className="flex items-center gap-3">
            <Typography variant="mediumParagraph" color="gray">
              No practice bot exists for this session yet
            </Typography>
            <button
              onClick={() => createPracticeBot(sessionData)}
              className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              <Typography variant="smallParagraph" color="white">
                Create Practice Bot
              </Typography>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the upgrade modal
  const renderUpgradeModal = () => {
    if (!showUpgradeModal) return null;
    
    const hasAlreadyPaid = user?.autodialer_paid || team?.subscription_type === SubscriptionType.TEAM;
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={() => setShowUpgradeModal(false)}
      >
        <div 
          className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              {hasAlreadyPaid ? 'Additional Access Required' : 'Upgrade Required'}
            </h3>
            <button 
              onClick={() => setShowUpgradeModal(false)}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              {hasAlreadyPaid 
                ? 'Your current package has limited access to Practice Mode. Schedule a call with us to learn more about upgrading your package and unlocking AI-powered sales role-play capabilities.'
                : "You've reached your practice call limit. Upgrade your subscription to continue practicing with AI-powered sales role-play."
              }
            </p>
            {!hasAlreadyPaid && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-blue-800 font-medium mb-2">Benefits of upgrading:</h4>
                <ul className="text-blue-700 text-sm space-y-2">
                  <li>• Unlimited practice calls</li>
                  <li>• Advanced conversation scenarios</li>
                  <li>• Performance analytics</li>
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              {hasAlreadyPaid ? 'Close' : 'Maybe Later'}
            </button>
            {hasAlreadyPaid ? (
              <a
                href="https://calendly.com/ajinkya_nene/general-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                onClick={() => setShowUpgradeModal(false)}
              >
                Schedule a Call
              </a>
            ) : (
              <a
                href={PaymentSubscriptionOptionToPaymentLink[PaymentSubscriptionOption.PARALLEL]}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                onClick={() => setShowUpgradeModal(false)}
              >
                Upgrade Now
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render the practice call modal
  const renderPracticeModal = () => {
    if (!showPracticeModal) return null;
    
    return (
      <div className="fixed inset-0 bg-gray-900/95 z-50 flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <h2 className="text-white font-semibold">Practice Call with {bot.name}</h2>
          </div>
          <button 
            onClick={handleEndCall}
            className="p-2 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors"
          >
            <PhoneOff size={20} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto bg-gray-800 p-4">
          <div className="max-w-3xl mx-auto space-y-4">
            {transcripts.length > 0 ? (
              transcripts.map((msg, index) => (
                <div 
                  key={index} 
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  ref={index === transcripts.length - 1 ? messagesEndRef : null}
                >
                  <div className={`
                    max-w-[80%] p-3 rounded-lg shadow-sm
                    ${msg.role === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-none' 
                      : 'bg-gray-700 text-white rounded-bl-none'
                    }
                  `}>
                    <div className="text-xs opacity-60 mb-1 font-medium">
                      {msg.role === 'assistant' ? bot.name : 'You'}
                    </div>
                    <div className="text-sm">
                      {msg.transcript}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400 py-8">
                <Mic size={32} className="mx-auto mb-2 opacity-50" />
                <p>Your practice call is now active. Speak naturally as if talking to a real prospect.</p>
                <p className="mt-2 text-sm text-gray-500">The AI will respond to your voice in real-time</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-700 bg-gray-900">
          <div className="max-w-3xl mx-auto space-y-4">
            {!isCallActive ? (
              <div className="flex flex-col gap-4">
                <AudioDeviceSelector
                  selectedInputDevice={selectedInputDevice}
                  selectedOutputDevice={selectedOutputDevice}
                  isCallActive={isCallActive}
                  onSelectInput={handleSelectAudioInput}
                  onSelectOutput={handleSelectAudioOutput}
                />
                
                <div className="flex justify-between">
                  <div className="space-x-2">
                    <select
                      value={selectedDifficulty}
                      onChange={(e) => setSelectedDifficulty(e.target.value as CallDifficultyMode)}
                      disabled={isCallActive}
                      className="bg-gray-800 text-white border border-gray-700 rounded px-3 py-1 text-sm"
                    >
                      {Object.entries(CALL_DIFFICULTY_TO_HUMAN_READABLE).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                    
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value as CallLanguage)}
                      disabled={isCallActive}
                      className="bg-gray-800 text-white border border-gray-700 rounded px-3 py-1 text-sm"
                    >
                      {Object.entries(LANGUAGE_TO_HUMAN_READABLE).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : null}
            
            <div className="flex justify-center">
              <button
                onClick={handleEndCall}
                className="px-6 py-3 rounded-full flex items-center gap-2 text-white transition-colors bg-red-600 hover:bg-red-700"
              >
                <PhoneOff size={18} />
                End Call
              </button>
            </div>
            
            <div className="mt-2 text-center text-xs text-gray-400">
              <p>Speak naturally to interact with {bot.name}. The AI will listen and respond to you.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Bot exists - show bot info with difficulty and language selectors (cleaner layout to match screenshot)
  return (
    <div className="px-3 py-2">
      <div className="whitespace-pre-wrap text-gray-700 p-3 bg-white rounded-lg border border-gray-100 relative">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Typography variant="mediumParagraph" fontWeight="600">
              Practice Bot Ready
            </Typography>
            <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">Active</span>
          </div>
          
          <Typography variant="mediumParagraph">
            Your practice bot has been trained on this conversation and is ready to use. 
            Use it to practice your pitch, objection handling, and discovery questions.
          </Typography>
          
          <div className="flex flex-wrap items-center justify-between mt-4">
            {/* Settings row with difficulty and language selectors */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" color="gray">
                  Difficulty:
                </Typography>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value as CallDifficultyMode)}
                  className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white text-gray-700 min-w-[100px]"
                >
                  {Object.entries(CALL_DIFFICULTY_TO_HUMAN_READABLE).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" color="gray">
                  Language:
                </Typography>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value as CallLanguage)}
                  className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white text-gray-700 min-w-[100px]"
                >
                  {Object.entries(LANGUAGE_TO_HUMAN_READABLE).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Start Practice button at far right */}
            <button 
              onClick={handleStartPractice}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              disabled={isInitializingCall}
            >
              {isInitializingCall ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <Typography variant="smallParagraph" color="white">
                    Initializing...
                  </Typography>
                </>
              ) : (
                <>
                  <Play size={16} className="text-white" />
                  <Typography variant="smallParagraph" color="white">
                    Start Practice
                  </Typography>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Access upgrade modal */}
      {renderUpgradeModal()}
      
      {/* Practice call modal */}
      {renderPracticeModal()}
  </div>
  );
}; 